import { nextTick } from 'vue'

/**
 * Envuelve un `refetch` de vue-query para que espere a que la llave se ponga al día.
 *
 * Vive en su propio archivo, sin dependencias de la app, para poder importarlo desde una
 * prueba: envuelto dentro de `useUser` no había forma de fijarlo, y una prueba que describe
 * a la librería en vez de a nuestro código pasa igual con el bug adentro.
 *
 * El porqué: los llamadores apuntan el composable a otro usuario asignando el id y pidiendo
 * refetch en la misma línea.
 *
 *     supervisorId.value = newUser.supervisorId
 *     refetchSupervisor()
 *
 * El ref cambia al instante, pero vue-query reacciona a la llave con un `watch` de flush
 * `pre` (useBaseQuery.js:42), o sea en el siguiente tick. Sin esperarlo, el refetch corre
 * contra la llave VIEJA y guarda ahí el resultado; un instante después el observador se
 * mueve a la llave nueva, que está vacía. Y como esas instancias van con `enabled: false`,
 * nadie vuelve a consultar: la pantalla se queda en el esqueleto para siempre.
 */
export const refetchAfterTick =
	<F extends (...args: never[]) => unknown>(refetch: F) =>
	async (...args: Parameters<F>): Promise<ReturnType<F>> => {
		await nextTick()

		return refetch(...args) as ReturnType<F>
	}
